import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, BookOpen, MessageSquare, Newspaper, Mail, CreditCard, GraduationCap, Trash2, Plus, Edit3, Send, Radio, FileText } from "lucide-react";
import AdminForm from "@/components/AdminForm";
import BotStatusCard from "@/components/BotStatusCard";

const TABS = ["Overview", "Lessons", "Courses", "Prompts", "Blog", "News", "Users", "Enrollments", "Payments", "Newsletter"];

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("Overview");
  const [stats, setStats] = useState(null);
  const [courses, setCourses] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [blog, setBlog] = useState([]);
  const [news, setNews] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [formKind, setFormKind] = useState(null);
  const [formInitial, setFormInitial] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [s, c, p, b, n, u, e, pay, nl, ls] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/courses"),
        api.get("/prompts"),
        api.get("/blog"),
        api.get("/news", { params: { limit: 200 } }),
        api.get("/admin/users"),
        api.get("/admin/enrollments"),
        api.get("/admin/payments"),
        api.get("/admin/newsletter"),
        api.get("/lessons"),
      ]);
      setStats(s.data); setCourses(c.data); setPrompts(p.data); setBlog(b.data); setNews(n.data);
      setUsers(u.data); setEnrollments(e.data); setPayments(pay.data); setNewsletter(nl.data);
      setLessons(ls.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Admin load failed");
    }
  }, []);

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user, refresh]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center text-slate-200">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-2">Admin access only</h1>
          <p className="text-slate-200">Your account doesn't have admin privileges.</p>
        </div>
      </div>
    );
  }

  const del = async (kind, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendNews = async (row) => {
    if (!window.confirm(`Email "${row.title}" to all ${newsletter.length} newsletter subscriber(s)?`)) return;
    try {
      const { data } = await api.post(`/admin/news/${row.id}/send`);
      toast.success(`Sent to ${data.sent} subscriber(s)`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-24 relative overflow-hidden" data-testid="admin-page">
      <div className="orb orb-violet w-[26rem] h-[26rem] -top-40 right-[15%] opacity-50" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline-label mb-2">Admin Panel</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter text-white">Control room</h1>
          </div>
          <div className="text-sm text-slate-200">Hi, <span className="text-white font-medium">{user.name}</span></div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-10 glass-panel rounded-full p-1.5">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                tab === t
                  ? "bg-white text-[#04121A]"
                  : "text-slate-200 hover:text-white hover:bg-white/10"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "Overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <BotStatusCard />
            {[
              { k: "users", label: "Users", icon: Users },
              { k: "lessons", label: "Lessons", icon: FileText },
              { k: "courses", label: "Courses", icon: BookOpen },
              { k: "prompts", label: "Prompts", icon: MessageSquare },
              { k: "blog", label: "Blog Posts", icon: Newspaper },
              { k: "enrollments", label: "Enrollments", icon: GraduationCap },
              { k: "payments", label: "Paid", icon: CreditCard },
              { k: "newsletter", label: "Subscribers", icon: Mail },
            ].map(s => (
              <div key={s.k} className="card-void p-6" data-testid={`stat-${s.k}`}>
                <s.icon className="text-orange-400 mb-3" size={18} aria-hidden="true" />
                <div className="font-display text-3xl font-bold text-white">{stats[s.k]}</div>
                <div className="text-[11px] uppercase tracking-widest text-slate-200 mt-1 font-semibold">{s.label}</div>
              </div>
            ))}
            <div className="card-void p-6" data-testid="stat-news">
              <Radio className="text-orange-400 mb-3" size={18} aria-hidden="true" />
              <div className="font-display text-3xl font-bold text-white">{news.length}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-200 mt-1 font-semibold">News Items</div>
            </div>
          </div>
        )}

        {tab === "Lessons" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setFormKind("lessons"); setFormInitial(null); }} className="btn-glow" data-testid="new-lesson-btn">
                <Plus size={14} /> New Lesson
              </button>
            </div>
            <DataTable
              kind="lessons"
              rows={lessons}
              cols={["title", "category", "published_at"]}
              onDelete={(id) => del("lessons", id)}
              onEdit={(row) => { setFormKind("lessons"); setFormInitial(row); }}
            />
          </>
        )}
        {tab === "Courses" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setFormKind("courses"); setFormInitial(null); }} className="btn-glow" data-testid="new-course-btn">
                <Plus size={14} /> New Course
              </button>
            </div>
            <DataTable
              kind="courses"
              rows={courses}
              cols={["title", "category", "level", "price_bdt", "duration_weeks", "coming_soon"]}
              onDelete={(id) => del("courses", id)}
              onEdit={(row) => { setFormKind("courses"); setFormInitial(row); }}
            />
          </>
        )}
        {tab === "Prompts" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setFormKind("prompts"); setFormInitial(null); }} className="btn-glow" data-testid="new-prompt-btn">
                <Plus size={14} /> New Prompt
              </button>
            </div>
            <DataTable
              kind="prompts"
              rows={prompts}
              cols={["title", "category", "tags"]}
              onDelete={(id) => del("prompts", id)}
              onEdit={(row) => { setFormKind("prompts"); setFormInitial(row); }}
            />
          </>
        )}
        {tab === "Blog" && (
          <>
            <div className="flex justify-end mb-4">
              <button onClick={() => { setFormKind("blog"); setFormInitial(null); }} className="btn-glow" data-testid="new-blog-btn">
                <Plus size={14} /> New Post
              </button>
            </div>
            <DataTable
              kind="blog"
              rows={blog}
              cols={["title", "author", "read_minutes", "published_at"]}
              onDelete={(id) => del("blog", id)}
              onEdit={(row) => { setFormKind("blog"); setFormInitial(row); }}
            />
          </>
        )}
        {tab === "News" && (
          <>
            <div className="flex items-center justify-between mb-4 gap-4">
              <p className="text-sm text-slate-200">
                Ticker items — use <Send size={12} className="inline text-orange-400" /> to email a link to all {newsletter.length} subscriber(s).
              </p>
              <button onClick={() => { setFormKind("news"); setFormInitial(null); }} className="btn-glow shrink-0" data-testid="new-news-btn">
                <Plus size={14} /> New News
              </button>
            </div>
            <DataTable
              kind="news"
              rows={news}
              cols={["title", "category", "source", "published_at", "email_sent_count"]}
              onDelete={(id) => del("news", id)}
              onEdit={(row) => { setFormKind("news"); setFormInitial(row); }}
              extra={(row) => (
                <button
                  onClick={() => sendNews(row)}
                  className="text-orange-400 hover:text-orange-400 p-2 mr-1 transition-colors"
                  title="Send to subscribers"
                  data-testid={`send-news-${row.id}`}
                >
                  <Send size={14} />
                </button>
              )}
            />
          </>
        )}
        {tab === "Users" && (
          <DataTable
            kind="users"
            rows={users}
            cols={["name", "email", "role", "created_at"]}
            readOnly
          />
        )}
        {tab === "Enrollments" && (
          <div className="card-void p-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-200">
                <th className="p-4">User</th><th className="p-4">Course</th><th className="p-4">Progress</th><th className="p-4">Enrolled</th>
              </tr></thead>
              <tbody>
                {enrollments.map(e => (
                  <tr key={e.enrollment.id} className="border-t border-white/[0.07]">
                    <td className="p-4 text-white">{e.user?.name} <span className="text-slate-200 text-xs block">{e.user?.email}</span></td>
                    <td className="p-4 text-slate-200">{e.course?.title}</td>
                    <td className="p-4 text-slate-200">{e.enrollment.progress || 0}%</td>
                    <td className="p-4 text-slate-200">{new Date(e.enrollment.enrolled_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "Payments" && (
          <div className="card-void p-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-200">
                <th className="p-4">Amount</th><th className="p-4">bKash</th><th className="p-4">TXN</th><th className="p-4">Status</th><th className="p-4">Date</th>
              </tr></thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id} className="border-t border-white/[0.07]">
                    <td className="p-4 font-mono text-white">৳{p.amount?.toLocaleString()}</td>
                    <td className="p-4 text-slate-200">{p.bkash_number}</td>
                    <td className="p-4 font-mono text-xs text-slate-200">{p.txn_id || "-"}</td>
                    <td className="p-4"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${p.status === "completed" ? "bg-emerald-500/10 text-emerald-300" : "bg-amber-500/10 text-amber-300"}`}>{p.status}</span></td>
                    <td className="p-4 text-slate-200">{new Date(p.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {tab === "Newsletter" && (
          <div className="card-void p-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-200">
                <th className="p-4">Email</th><th className="p-4">Subscribed</th>
              </tr></thead>
              <tbody>
                {newsletter.map(n => (
                  <tr key={n.email} className="border-t border-white/[0.07]">
                    <td className="p-4 text-white">{n.email}</td>
                    <td className="p-4 text-slate-200">{new Date(n.subscribed_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formKind && (
        <AdminForm
          kind={formKind}
          initial={formInitial}
          onClose={() => { setFormKind(null); setFormInitial(null); }}
          onSaved={refresh}
        />
      )}
    </div>
  );
}

function DataTable({ kind, rows, cols, readOnly, onDelete, onEdit, extra }) {
  return (
    <div className="card-void p-2 overflow-x-auto" data-testid={`admin-table-${kind}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-widest text-slate-200">
            {cols.map(c => <th key={c} className="p-4">{c.replace(/_/g, ' ')}</th>)}
            {!readOnly && <th className="p-4 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id || r.email} className="border-t border-white/[0.07]">
              {cols.map(c => (
                <td key={c} className="p-4 max-w-xs truncate text-slate-200">
                  {Array.isArray(r[c]) ? r[c].join(", ") : typeof r[c] === "boolean" ? (r[c] ? "Yes" : "No") : String(r[c] ?? "-")}
                </td>
              ))}
              {!readOnly && (
                <td className="p-4 text-right whitespace-nowrap">
                  {extra && extra(r)}
                  {onEdit && (
                    <button
                      onClick={() => onEdit(r)}
                      className="text-slate-200 hover:text-orange-300 p-2 mr-1 transition-colors"
                      data-testid={`edit-${kind}-${r.id}`}
                    >
                      <Edit3 size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(r.id)}
                    className="text-red-400 hover:text-red-300 p-2 transition-colors"
                    data-testid={`delete-${kind}-${r.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td className="p-8 text-center text-slate-200" colSpan={cols.length + 1}>No data</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
